import PropTypes from "prop-types"
import React from "react"
import { usePageAssets } from "hooks/usePageAssets"

const LOGO_PREFIX = "home-logo-ticker-"
const BASE_SPEED = 30
const BASE_COUNT = 6

// Case study links keyed by asset name
const LOGO_LINKS = {
  "home-logo-ticker-3": "/blog/customer-spotlight-rocket-travel-by-agoda/",
  "home-logo-ticker-4":
    "/blog/transitioning-from-looker-to-preset-to-enable-self-serve-analytics/",
  "home-logo-ticker-5": "/blog/open-table/",
  "home-logo-ticker-7":
    "/blog/empowering-educational-leaders-with-data-driven-insights/",
  "home-logo-ticker-8": "/blog/case-study-flywire/",
  "home-logo-ticker-9": "/blog/customer-spotlight-metamap/",
}

const CARD_CLASS =
  "flex h-12 w-[140px] flex-shrink-0 items-center justify-center px-5 lg:h-16 lg:w-[180px] lg:px-6"

function LogoCard({ logo, hideAlt }) {
  const img = (
    <img
      src={logo.url}
      alt={hideAlt ? "" : logo.alt || ""}
      className="h-full w-full object-contain"
      loading="eager"
    />
  )

  if (logo.link) {
    return (
      <a
        href={logo.link}
        className={`${CARD_CLASS} scale-90 transition-transform hover:scale-100`}
      >
        {img}
      </a>
    )
  }

  return <div className={CARD_CLASS}>{img}</div>
}

function LogoTicker({
  logos: logoProp,
  speed,
  bgColor = "bg-drift-grey",
  className = "",
  pauseOnHover = true,
}) {
  const { assets } = usePageAssets()

  const logos = logoProp
    ? logoProp
    : assets
        .filter((a) => a.name.startsWith(LOGO_PREFIX))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((a) => ({
          url: a.publicURL,
          alt: a.name.replace(LOGO_PREFIX, "").replace(/-/g, " "),
          link: LOGO_LINKS[a.name],
        }))

  if (!logos.length) return null

  const duration = speed || (BASE_SPEED * logos.length) / BASE_COUNT

  const hoverClass = pauseOnHover ? "hover:[animation-play-state:paused]" : ""

  return (
    <section
      aria-label="Trusted by leading companies"
      className={`${bgColor} overflow-hidden ${className}`}
    >
      <div className="py-6 lg:py-8">
        <div
          className={`animate-ticker flex motion-reduce:[animation-play-state:paused] ${hoverClass}`}
          style={{
            "--ticker-duration": `${duration}s`,
            width: "max-content",
          }}
        >
          <div className="flex items-center">
            {logos.map((logo, i) => (
              <LogoCard key={`logo-${i}`} logo={logo} />
            ))}
          </div>
          <div className="flex items-center" aria-hidden="true">
            {logos.map((logo, i) => (
              <LogoCard key={`logo-dup-${i}`} logo={logo} hideAlt />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

LogoTicker.propTypes = {
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  speed: PropTypes.number,
  bgColor: PropTypes.string,
  className: PropTypes.string,
  pauseOnHover: PropTypes.bool,
}

export default LogoTicker
